import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, UserRound, KeyRound } from "lucide-react";
import InvitationPanel from "@/components/InvitationPanel";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      await http.post("/users", payload);
      toast.success("Collaborateur créé");
      setCreating(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, payload) => {
    setSaving(true);
    try {
      await http.patch(`/users/${id}`, payload);
      toast.success("Modifications enregistrées");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await http.delete(`/users/${deleting.id}`);
      toast.success("Collaborateur supprimé");
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Layout>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" data-testid="admin-users">
        <div>
          <p className="label-eyebrow mb-2">Équipe</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Collaborateurs</h1>
          <p className="mt-2 text-slate-600">Partagez le lien d'invitation ci-dessous ou ajoutez manuellement un compte.</p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="add-user-button" size="lg" className="gap-2" variant="outline">
          <Plus className="h-4 w-4" /> Ajouter manuellement
        </Button>
      </div>

      <InvitationPanel />

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : users.length === 0 ? (
        <Card className="p-12 text-center text-slate-500">Aucun collaborateur. Ajoutez le premier pour commencer.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((u) => (
            <Card key={u.id} className="p-5 hover:shadow-md transition-all duration-200" data-testid={`user-card-${u.id}`}>
              <div className="flex items-start gap-3 mb-4">
                <div className="h-11 w-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display font-bold">
                  {u.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-slate-900 truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
                <Badge variant={u.role === "admin" ? "default" : "secondary"} className="capitalize">
                  {u.role === "admin" ? "Admin" : "Négociateur"}
                </Badge>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Modifier
                </Button>
                {u.role !== "admin" && (
                  <Button variant="outline" size="sm" className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleting(u)} data-testid={`delete-user-${u.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <UserFormDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSubmit={handleCreate}
        saving={saving}
        title="Ajouter un collaborateur"
        description="Créez un compte pour un membre de votre équipe."
        submitLabel="Créer le compte"
      />

      <UserFormDialog
        open={!!editing}
        initial={editing}
        onClose={() => setEditing(null)}
        onSubmit={(payload) => handleUpdate(editing.id, payload)}
        saving={saving}
        title="Modifier le collaborateur"
        description="Mettez à jour les informations du compte."
        submitLabel="Enregistrer"
        editMode
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce collaborateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le compte de <strong>{deleting?.name}</strong> et tous ses rapports hebdomadaires seront supprimés définitivement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function UserFormDialog({ open, onClose, onSubmit, saving, title, description, submitLabel, initial, editMode }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "collaborator" });

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? { email: initial.email, name: initial.name, password: "", role: initial.role }
          : { email: "", name: "", password: "", role: "collaborator" }
      );
    }
  }, [open, initial]);

  const submit = (e) => {
    e.preventDefault();
    const payload = editMode
      ? { name: form.name, role: form.role, ...(form.password ? { password: form.password } : {}) }
      : form;
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Nom complet</Label>
            <div className="relative">
              <UserRound className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 pl-9" data-testid="user-form-name" placeholder="Marie Dupont" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Email</Label>
            <Input required type="email" disabled={editMode} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="user-form-email" placeholder="marie@immo.fr" />
            {editMode && <p className="text-xs text-slate-400">L'email ne peut pas être modifié.</p>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
              {editMode ? "Nouveau mot de passe (facultatif)" : "Mot de passe"}
            </Label>
            <div className="relative">
              <KeyRound className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input required={!editMode} type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 pl-9" data-testid="user-form-password" placeholder="Min. 6 caractères" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Rôle</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="collaborator">Négociateur</SelectItem>
                <SelectItem value="admin">Administrateur</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="user-form-submit" className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
